import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import ModalWrapper, { ModalCard } from '../ModalWrapper';

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
`;

const TypeButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  background: #0a0a0a;
  border: 2px solid ${props => props.$isSelected ? '#0099ff' : '#333'};
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.$isSelected ? '#0099ff' : '#555'};
    background: #151515;
  }
`;

const TypeIcon = styled.span`
  font-size: 24px;
`;

const TypeLabel = styled.span`
  font-size: 12px;
  color: #999;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #999;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #0099ff;
  }

  &::placeholder {
    color: #555;
  }
`;

const DurationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DurationInput = styled.input`
  width: 80px;
  padding: 12px 16px;
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 16px;
  font-family: 'JetBrains Mono', monospace;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #0099ff;
  }
`;

const DurationSeparator = styled.span`
  color: #666;
  font-size: 20px;
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #999;
  margin-bottom: 12px;

  input {
    width: 18px;
    height: 18px;
    accent-color: #0099ff;
  }
`;

const TemplateGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TemplateButton = styled.button`
  padding: 8px 16px;
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 20px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0099ff;
    background: rgba(0, 153, 255, 0.1);
  }
`;

const PreviewBox = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
`;

const PreviewIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const PreviewTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
`;

const PreviewSubtitle = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
`;

const PreviewDuration = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 28px;
  color: #0099ff;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #333;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  ${props => props.$variant === 'primary' && css`
    background: #0099ff;
    border: none;
    color: #fff;

    &:hover {
      background: #007acc;
    }
  `}

  ${props => props.$variant === 'secondary' && css`
    background: transparent;
    border: 1px solid #333;
    color: #fff;

    &:hover {
      border-color: #555;
    }
  `}
`;

const INTERLUDE_TYPES = [
  { id: 'break', icon: '☕', label: 'Tauko' },
  { id: 'countdown', icon: '⏳', label: 'Countdown' },
  { id: 'message', icon: '📢', label: 'Viesti' },
  { id: 'celebration', icon: '🎉', label: 'Juhla' },
];

const DEFAULT_TEMPLATES = [
  { name: 'Kahvitauko 15min', type: 'break', title: 'Kahvitauko', minutes: 15 },
  { name: 'Lounas 45min', type: 'break', title: 'Lounastauko', minutes: 45 },
  { name: 'Countdown 60s', type: 'countdown', title: 'Seuraava vaihe alkaa', minutes: 1 },
  { name: 'Countdown 10s', type: 'countdown', title: 'Aloitetaan!', minutes: 0, seconds: 10 },
];

const TYPE_ICONS = {
  break: '☕',
  countdown: '⏳',
  message: '📢',
  transition: '▶',
  celebration: '🎉',
};

/**
 * InterludeModal - Admin modal for configuring and starting interludes
 *
 * @param {boolean} visible - Whether modal is visible
 * @param {function} onClose - Close callback
 * @param {function} onStart - Start interlude callback with config
 * @param {array} templates - Saved templates
 */
const InterludeModal = ({
  visible = false,
  onClose,
  onStart,
  templates = DEFAULT_TEMPLATES,
}) => {
  const [type, setType] = useState('break');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [minutes, setMinutes] = useState(15);
  const [seconds, setSeconds] = useState(0);
  const [showProgress, setShowProgress] = useState(true);
  const [showReturnTime, setShowReturnTime] = useState(true);
  const [playSound, setPlaySound] = useState(true);

  const handleTemplateClick = (template) => {
    setType(template.type);
    setTitle(template.title);
    setMinutes(template.minutes || 0);
    setSeconds(template.seconds || 0);
  };

  const handleStart = () => {
    const duration = (minutes * 60) + seconds;
    onStart?.({
      type,
      title: title || INTERLUDE_TYPES.find(t => t.id === type)?.label || 'Interlude',
      subtitle,
      duration: duration > 0 ? duration : null,
      showProgress,
      showReturnTime,
      playSound,
    });
    onClose?.();
  };

  const formatDuration = () => {
    const totalSeconds = (minutes * 60) + seconds;
    if (totalSeconds === 0) return '--:--';
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <ModalWrapper visible={visible} onClose={onClose} zIndex={1000} opacity={0.8} blur={0}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()} style={{ background: '#1a1a1a', border: '1px solid #333' }}>
        <ModalCard.Header>
          <h3>Start Interlude</h3>
        </ModalCard.Header>

        <ModalCard.Body>
          <Section>
            <SectionLabel>Type</SectionLabel>
            <TypeGrid>
              {INTERLUDE_TYPES.map((t) => (
                <TypeButton
                  key={t.id}
                  $isSelected={type === t.id}
                  onClick={() => setType(t.id)}
                >
                  <TypeIcon>{t.icon}</TypeIcon>
                  <TypeLabel>{t.label}</TypeLabel>
                </TypeButton>
              ))}
            </TypeGrid>
          </Section>

          <Section>
            <InputGroup>
              <Label>Title</Label>
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={INTERLUDE_TYPES.find(t => t.id === type)?.label || 'Interlude'}
              />
            </InputGroup>

            <InputGroup>
              <Label>Subtitle (optional)</Label>
              <Input
                type="text"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Additional message..."
              />
            </InputGroup>

            <InputGroup>
              <Label>Duration</Label>
              <DurationRow>
                <DurationInput
                  type="number"
                  min="0"
                  max="99"
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                />
                <DurationSeparator>:</DurationSeparator>
                <DurationInput
                  type="number"
                  min="0"
                  max="59"
                  value={seconds}
                  onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
                />
                <span style={{ color: '#666', fontSize: '13px' }}>mm:ss</span>
              </DurationRow>
            </InputGroup>
          </Section>

          <Section>
            <SectionLabel>Options</SectionLabel>
            <Checkbox>
              <input
                type="checkbox"
                checked={showProgress}
                onChange={(e) => setShowProgress(e.target.checked)}
              />
              Show progress bar
            </Checkbox>
            <Checkbox>
              <input
                type="checkbox"
                checked={showReturnTime}
                onChange={(e) => setShowReturnTime(e.target.checked)}
              />
              Show return time
            </Checkbox>
            <Checkbox>
              <input
                type="checkbox"
                checked={playSound}
                onChange={(e) => setPlaySound(e.target.checked)}
              />
              Play sound at end
            </Checkbox>
          </Section>

          <Section>
            <SectionLabel>Quick Templates</SectionLabel>
            <TemplateGrid>
              {templates.map((template, i) => (
                <TemplateButton key={i} onClick={() => handleTemplateClick(template)}>
                  {template.name}
                </TemplateButton>
              ))}
            </TemplateGrid>
          </Section>

          <Section>
            <SectionLabel>Preview</SectionLabel>
            <PreviewBox>
              <PreviewIcon>{TYPE_ICONS[type]}</PreviewIcon>
              <PreviewTitle>
                {title || INTERLUDE_TYPES.find(t => t.id === type)?.label}
              </PreviewTitle>
              {subtitle && <PreviewSubtitle>{subtitle}</PreviewSubtitle>}
              <PreviewDuration>{formatDuration()}</PreviewDuration>
            </PreviewBox>
          </Section>
        </ModalCard.Body>

        <Footer>
          <Button $variant="secondary" onClick={onClose}>Cancel</Button>
          <Button $variant="primary" onClick={handleStart}>
            Start Interlude
          </Button>
        </Footer>
      </ModalCard>
    </ModalWrapper>
  );
};

export default InterludeModal;
